import { useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  Wrap,
  WrapItem,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { Avatar } from '../Avatar';
import { currentFamilyIdAtom } from '../../store';
import { Trash2 } from 'lucide-react';
import { LIST_REPEATS, isoToday, type ListRepeat } from '@twinden/shared';
import type { FamilyListItem, FamilyMember } from '../../lib/api';
import { REPEAT_LABELS } from '../../lib/lists';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';

export interface ListItemChanges {
  text: string;
  note: string | null;
  assignedTo: string | null;
  dueDate: string | null;
  repeat: ListRepeat | null;
}

function Choice({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      h="36px"
      px={3}
      fontSize="14px"
      fontWeight={600}
      colorScheme="gray"
      bg={selected ? 'ink' : 'surface'}
      color={selected ? 'canvas' : 'ink'}
      borderWidth={selected ? 0 : '1px'}
      borderColor="line"
      aria-pressed={selected}
      _hover={{ opacity: 0.9 }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function addDays(iso: string, days: number) {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// An item's details (M26): who it's for (D45), when (D46), how often (D47).
export function ListItemDetails({
  item,
  members,
  currentUserId,
  isSaving,
  onSave,
  onDelete,
  onClose,
}: {
  item: FamilyListItem | null;
  members: FamilyMember[];
  currentUserId: string | undefined;
  isSaving: boolean;
  onSave: (changes: ListItemChanges) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const t = useT();
  // Faces open through the family whose list this is (D292).
  const familyId = useAtomValue(currentFamilyIdAtom);
  const sheet = useBreakpointValue({ base: true, md: false }) ?? true;
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [repeat, setRepeat] = useState<ListRepeat | null>(null);

  useEffect(() => {
    if (!item) return;
    setText(item.text);
    setNote(item.note ?? '');
    setAssignedTo(item.assignedTo ?? null);
    setDueDate(item.dueDate ?? null);
    setRepeat(item.repeat ?? null);
  }, [item]);

  const today = isoToday();
  const lastDoneDate = item?.lastDoneAt
    ? new Date(item.lastDoneAt).toLocaleDateString(appLocale(), {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : '';
  const lastDone =
    item?.lastDoneAt && item.lastDoneByUser
      ? item.lastDoneBy === currentUserId
        ? t('apps.listItem.lastDoneByYou', { date: lastDoneDate })
        : t('apps.listItem.lastDoneBy', {
            name: item.lastDoneByUser.displayName.split(' ')[0] ?? '',
            date: lastDoneDate,
          })
      : null;

  const content = item && (
    <Stack spacing={5}>
      <Heading as="h2" fontSize="22px">
        {t('apps.listItem.title')}
      </Heading>
      <FormControl>
        <FormLabel>{t('apps.listItem.item')}</FormLabel>
        <Input
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={300}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('apps.listItem.note')}</FormLabel>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={500}
          rows={2}
          placeholder={t('apps.listItem.notePlaceholder')}
        />
      </FormControl>

      <Box>
        <Text fontWeight={600} mb={2}>
          {t('apps.listItem.for')}
        </Text>
        <Wrap spacing={2}>
          <WrapItem>
            <Choice
              selected={assignedTo === null}
              onClick={() => setAssignedTo(null)}
            >
              {t('apps.listItem.anyone')}
            </Choice>
          </WrapItem>
          {members.map((member) => (
            <WrapItem key={member.userId}>
              <Choice
                selected={assignedTo === member.userId}
                onClick={() => setAssignedTo(member.userId)}
              >
                <HStack spacing={2}>
                  <Avatar
                    size="2xs"
                    name={member.user.displayName}
                    src={member.user.avatarUrl ?? undefined}
                    ownerId={member.userId}
                    familyId={familyId}
                  />
                  <span>
                    {member.userId === currentUserId
                      ? t('apps.listItem.me')
                      : (member.nickname ??
                        member.user.displayName.split(' ')[0])}
                  </span>
                </HStack>
              </Choice>
            </WrapItem>
          ))}
        </Wrap>
      </Box>

      <Box>
        <Text fontWeight={600} mb={2}>
          {t('apps.listItem.due')}
        </Text>
        <Wrap spacing={2} align="center">
          <WrapItem>
            <Choice
              selected={dueDate === null}
              onClick={() => setDueDate(null)}
            >
              {t('apps.listItem.noDate')}
            </Choice>
          </WrapItem>
          <WrapItem>
            <Choice
              selected={dueDate === today}
              onClick={() => setDueDate(today)}
            >
              {t('apps.shared.today')}
            </Choice>
          </WrapItem>
          <WrapItem>
            <Choice
              selected={dueDate === addDays(today, 1)}
              onClick={() => setDueDate(addDays(today, 1))}
            >
              {t('apps.listItem.tomorrow')}
            </Choice>
          </WrapItem>
          <WrapItem>
            <Input
              type="date"
              aria-label={t('apps.listItem.dueDate')}
              h="36px"
              w="auto"
              value={dueDate ?? ''}
              onChange={(event) => setDueDate(event.target.value || null)}
            />
          </WrapItem>
        </Wrap>
      </Box>

      <Box>
        <Text fontWeight={600} mb={2}>
          {t('apps.listItem.repeats')}
        </Text>
        <Wrap spacing={2}>
          <WrapItem>
            <Choice selected={repeat === null} onClick={() => setRepeat(null)}>
              {t('apps.listItem.never')}
            </Choice>
          </WrapItem>
          {LIST_REPEATS.map((value) => (
            <WrapItem key={value}>
              <Choice
                selected={repeat === value}
                onClick={() => setRepeat(value)}
              >
                {t(REPEAT_LABELS[value])}
              </Choice>
            </WrapItem>
          ))}
        </Wrap>
        {repeat && (
          <Text fontSize="14px" color="muted" pt={2}>
            {t('apps.listItem.repeatHint')}
            {lastDone ? ` ${lastDone}.` : ''}
          </Text>
        )}
      </Box>

      <Stack spacing="10px" pt={1}>
        <Button
          h="52px"
          isLoading={isSaving}
          isDisabled={!text.trim()}
          onClick={() =>
            onSave({
              text: text.trim(),
              note: note.trim() || null,
              assignedTo,
              dueDate: dueDate ?? (repeat ? today : null),
              repeat,
            })
          }
        >
          {t('common.save')}
        </Button>
        <Button
          h="52px"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          bg="surface"
          color="danger"
          leftIcon={<Trash2 size={18} />}
          onClick={onDelete}
        >
          {t('apps.listItem.delete')}
        </Button>
      </Stack>
    </Stack>
  );

  if (sheet) {
    return (
      <Drawer isOpen={item !== null} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          maxH="92vh"
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px">
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="gray.200"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={item !== null} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent borderRadius="28px" bg="surface">
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
